// client/src/utils/notifications.js
import toast from 'react-hot-toast';

// Custom notification styles
const notificationStyles = {
  success: {
    style: {
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)',
      color: 'white',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 0 15px rgba(16, 185, 129, 0.3)',
    },
    iconTheme: {
      primary: 'white',
      secondary: 'rgba(16, 185, 129, 1)',
    },
  },
  error: {
    style: {
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
      color: 'white',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 0 15px rgba(239, 68, 68, 0.3)',
    },
    iconTheme: {
      primary: 'white',
      secondary: 'rgba(239, 68, 68, 1)',
    },
  },
  warning: {
    style: {
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%)',
      color: 'white',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 0 15px rgba(245, 158, 11, 0.3)',
    },
    iconTheme: {
      primary: 'white',
      secondary: 'rgba(245, 158, 11, 1)',
    },
  },
  info: {
    style: {
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%)',
      color: 'white',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 0 15px rgba(59, 130, 246, 0.3)',
    },
    iconTheme: {
      primary: 'white',
      secondary: 'rgba(59, 130, 246, 1)',
    },
  }
};

// Simplified notification utility functions
export const notify = {
  success: (message, options = {}) => {
    return toast.success(message, {
      duration: 3000,
      ...options
    });
  },

  error: (message, options = {}) => {
    return toast.error(message, {
      duration: 3000,
      ...options
    });
  },

  warning: (message, options = {}) => {
    return toast(message, {
      duration: 3000,
      icon: '⚠️',
      style: {
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)',
        color: 'white',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '12px',
        padding: '16px 20px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      },
      ...options
    });
  },

  info: (message, options = {}) => {
    return toast(message, {
      duration: 3000,
      icon: 'ℹ️',
      style: {
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%)',
        color: 'white',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '16px 20px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      },
      ...options
    });
  },

  promise: (promise, messages, options = {}) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading || 'Processing...',
        success: messages.success || 'Success!',
        error: messages.error || 'Something went wrong',
      },
      {
        ...options
      }
    );
  },

  // Special notification for complex errors with suggestions
  errorWithSuggestions: (error, errorCategory, suggestions = []) => {
    const getIcon = () => {
      switch (errorCategory) {
        case 'account_claimed':
        case 'duplicate_passport':
        case 'rate_limit':
          return '⚠️';
        case 'unauthorized':
          return '🔒';
        case 'network':
          return '🌐';
        default:
          return '❌';
      }
    };

    const getTitle = () => {
      switch (errorCategory) {
        case 'account_claimed':
          return 'Account Already Claimed';
        case 'duplicate_passport':
          return 'Passport Already Exists';
        case 'rate_limit':
          return 'Creation Limit Reached';
        case 'unauthorized':
          return 'Not Authorized';
        case 'network':
          return 'Network Error';
        default:
          return 'Verification Failed';
      }
    };

    const isWarning = ['account_claimed', 'duplicate_passport', 'rate_limit'].includes(errorCategory);
    const style = isWarning ? notificationStyles.warning : notificationStyles.error;

    return toast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'}`}
        style={{
          background: isWarning 
            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)',
          color: 'white',
          border: isWarning 
            ? '1px solid rgba(245, 158, 11, 0.3)'
            : '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px 20px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          maxWidth: '400px',
          minWidth: '300px',
          animation: t.visible ? 'toast-enter 0.3s ease-out' : 'toast-leave 0.3s ease-in forwards'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px', flexShrink: 0, lineHeight: '1' }}>
            {getIcon()}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: '600', 
              marginBottom: '4px',
              fontSize: '15px',
              lineHeight: '1.2'
            }}>
              {getTitle()}
            </div>
            <div style={{ 
              fontSize: '13px', 
              opacity: 0.9,
              marginBottom: suggestions.length > 0 ? '8px' : '0'
            }}>
              {error}
            </div>
            {suggestions.length > 0 && (
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {suggestions.slice(0, 2).map((suggestion, index) => (
                  <div key={index} style={{ marginBottom: '2px' }}>
                    • {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
              padding: '4px 6px',
              fontSize: '12px',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              lineHeight: '1',
              height: '24px',
              width: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'flex-start'
            }}
            onMouseOver={(e) => e.target.style.opacity = '1'}
            onMouseOut={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>
      </div>
    ), {
      duration: 3000,
      position: 'bottom-left'
    });
  },

  // Copy to clipboard notification
  copied: (text = 'Copied to clipboard!') => {
    return toast.success(text, {
      duration: 2000,
      icon: '📋'
    });
  }
};

// Add CSS animations and positioning overrides - FIXED VERSION
const style = document.createElement('style');
style.textContent = `
  /* Ensure your main site container has position: relative */
  /* Add this to your main app container CSS: */
  /*
  .app-container, #root, body {
    position: relative;
  }
  */

  /* Toast positioning override - Changed from fixed to absolute */
  .toast-container {
    position: absolute !important;
    bottom: 20px !important;
    left: 20px !important;
    top: unset !important;
    right: unset !important;
    z-index: 9999 !important;
  }

  /* Toast positioning for react-hot-toast - Changed from fixed to absolute */
  [data-hot-toast] {
    position: absolute !important;
    bottom: 20px !important;
    left: 20px !important;
    top: unset !important;
    right: unset !important;
    z-index: 9999 !important;
  }

  /* Individual toast positioning */
  [data-hot-toast] > div {
    position: relative !important;
    margin-bottom: 12px !important;
  }

  @keyframes toast-enter {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes toast-leave {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(-100%);
      opacity: 0;
    }
  }

  /* Ensure toasts appear from bottom-left */
  [data-hot-toast] [data-hot-toast-item] {
    animation: toast-enter 0.3s ease-out;
  }

  [data-hot-toast] [data-hot-toast-item][data-hot-toast-state="leave"] {
    animation: toast-leave 0.3s ease-in forwards;
  }
`;
document.head.appendChild(style);

export default notify;